import { useState, useEffect } from "react";
import { X, Loader2 } from "lucide-react";
import apiClient, { formatApiErrorDetail } from "@/lib/api";
import { todayISO } from "@/components/promo/promoMeta";

const EMPTY = {
  promo_name: "",
  promo_code: "",
  description: "",
  discount_type: "percentage",
  discount_value: "",
  max_discount_amount: "",
  start_date: todayISO(),
  end_date: todayISO(),
  usage_limit: "",
  usage_limit_per_customer: "",
  minimum_booking_amount: "",
  status: "active",
  property_id: "",
};

export default function PromoFormModal({ open, promo, properties, onClose, onSaved }) {
  const isEdit = !!promo;
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setError("");
      if (promo) {
        setForm({
          promo_name: promo.promo_name || "",
          promo_code: promo.promo_code || "",
          description: promo.description || "",
          discount_type: promo.discount_type || "percentage",
          discount_value: String(promo.discount_value ?? ""),
          max_discount_amount: promo.max_discount_amount != null ? String(promo.max_discount_amount) : "",
          start_date: (promo.start_date || todayISO()).slice(0, 10),
          end_date: (promo.end_date || todayISO()).slice(0, 10),
          usage_limit: promo.usage_limit != null ? String(promo.usage_limit) : "",
          usage_limit_per_customer: promo.usage_limit_per_customer != null ? String(promo.usage_limit_per_customer) : "",
          minimum_booking_amount: promo.minimum_booking_amount ? String(promo.minimum_booking_amount) : "",
          status: promo.status || "active",
          property_id: promo.property_id || "",
        });
      } else {
        setForm({ ...EMPTY, property_id: properties?.[0]?.id || "" });
      }
    }
  }, [open, promo, properties]);

  if (!open) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setError("");
    if (!form.promo_name.trim()) return setError("Nama promo wajib diisi.");
    if (!form.promo_code.trim()) return setError("Kode promo wajib diisi.");
    if (!form.property_id) return setError("Pilih property.");
    const dv = Number(form.discount_value);
    if (!dv || dv <= 0) return setError("Nilai diskon harus lebih dari 0.");
    if (form.discount_type === "percentage" && dv > 100) return setError("Diskon persentase tidak boleh lebih dari 100%.");
    if (form.end_date < form.start_date) return setError("Tanggal berakhir tidak boleh sebelum tanggal mulai.");

    const body = {
      promo_name: form.promo_name.trim(),
      promo_code: form.promo_code.trim(),
      description: form.description.trim() || null,
      discount_type: form.discount_type,
      discount_value: dv,
      max_discount_amount: form.max_discount_amount ? Number(form.max_discount_amount) : null,
      start_date: form.start_date,
      end_date: form.end_date,
      usage_limit: form.usage_limit ? Number(form.usage_limit) : null,
      usage_limit_per_customer: form.usage_limit_per_customer ? Number(form.usage_limit_per_customer) : null,
      minimum_booking_amount: form.minimum_booking_amount ? Number(form.minimum_booking_amount) : 0,
      status: form.status,
    };
    setSubmitting(true);
    try {
      if (isEdit) {
        await apiClient.put(`/owner/promos/${promo.id}`, body);
      } else {
        await apiClient.post("/owner/promos", { ...body, property_id: form.property_id });
      }
      onSaved?.();
      onClose?.();
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] grid place-items-center p-4" data-testid="promo-form-modal">
      <div className="absolute inset-0 bg-foreground/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-card border border-border rounded-2xl shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border sticky top-0 bg-card rounded-t-2xl z-10">
          <h3 className="font-heading text-lg font-semibold">{isEdit ? "Edit Promo" : "Buat Promo"}</h3>
          <button onClick={onClose} data-testid="promo-form-close" className="p-1.5 rounded-lg hover:bg-secondary/60 transition-colors"><X size={18} /></button>
        </div>

        <div className="p-6 space-y-4">
          {!isEdit && (
            <Field label="Property">
              <select value={form.property_id} onChange={(e) => set("property_id", e.target.value)}
                data-testid="promo-property" className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/30">
                <option value="">Pilih property…</option>
                {(properties || []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </Field>
          )}

          <div className="grid grid-cols-2 gap-3">
            <Field label="Nama Promo">
              <input value={form.promo_name} onChange={(e) => set("promo_name", e.target.value)}
                data-testid="promo-name" placeholder="Diskon Awal Tahun" className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/30" />
            </Field>
            <Field label="Kode Promo">
              <input value={form.promo_code} onChange={(e) => set("promo_code", e.target.value.toUpperCase())}
                data-testid="promo-code" placeholder="PROMO10" className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/30 uppercase" />
            </Field>
          </div>

          <Field label="Deskripsi (opsional)">
            <textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={2}
              data-testid="promo-description" className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/30" placeholder="Syarat & ketentuan singkat" />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Tipe Diskon">
              <select value={form.discount_type} onChange={(e) => set("discount_type", e.target.value)}
                data-testid="promo-type" className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/30">
                <option value="percentage">Persentase (%)</option>
                <option value="fixed_amount">Nominal Tetap (Rp)</option>
              </select>
            </Field>
            <Field label={form.discount_type === "percentage" ? "Nilai (%)" : "Nilai (Rp)"}>
              <input type="number" min={1} value={form.discount_value} onChange={(e) => set("discount_value", e.target.value)}
                data-testid="promo-value" className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/30" placeholder={form.discount_type === "percentage" ? "10" : "50000"} />
            </Field>
          </div>

          {form.discount_type === "percentage" && (
            <Field label="Maks. Potongan (Rp, opsional)">
              <input type="number" min={0} value={form.max_discount_amount} onChange={(e) => set("max_discount_amount", e.target.value)}
                data-testid="promo-max" className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/30" placeholder="Contoh: 200000" />
            </Field>
          )}

          <div className="grid grid-cols-2 gap-3">
            <Field label="Mulai">
              <input type="date" value={form.start_date} onChange={(e) => set("start_date", e.target.value)} data-testid="promo-start" className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/30" />
            </Field>
            <Field label="Berakhir">
              <input type="date" value={form.end_date} onChange={(e) => set("end_date", e.target.value)} data-testid="promo-end" className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/30" />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Batas Pakai Total (opsional)">
              <input type="number" min={1} value={form.usage_limit} onChange={(e) => set("usage_limit", e.target.value)} data-testid="promo-usage-limit" className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/30" placeholder="Tak terbatas" />
            </Field>
            <Field label="Batas / Customer (opsional)">
              <input type="number" min={1} value={form.usage_limit_per_customer} onChange={(e) => set("usage_limit_per_customer", e.target.value)} data-testid="promo-usage-per-customer" className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/30" placeholder="Tak terbatas" />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Min. Booking (Rp, opsional)">
              <input type="number" min={0} value={form.minimum_booking_amount} onChange={(e) => set("minimum_booking_amount", e.target.value)} data-testid="promo-min-booking" className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/30" placeholder="0" />
            </Field>
            <Field label="Status">
              <select value={form.status} onChange={(e) => set("status", e.target.value)} data-testid="promo-status" className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/30">
                <option value="active">Aktif</option>
                <option value="inactive">Nonaktif</option>
              </select>
            </Field>
          </div>

          {error && <div className="rounded-xl bg-destructive/10 border border-destructive/30 px-4 py-3 text-sm text-destructive" data-testid="promo-form-error">{error}</div>}

          <button onClick={submit} disabled={submitting} data-testid="promo-form-submit"
            className="w-full py-3 rounded-full bg-primary text-primary-foreground font-semibold hover:scale-[1.02] active:scale-95 transition-transform disabled:opacity-60 flex items-center justify-center gap-2">
            {submitting && <Loader2 size={16} className="animate-spin" />}
            {submitting ? "Menyimpan…" : isEdit ? "Simpan Perubahan" : "Buat Promo"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-sm font-medium mb-1.5">{label}</label>
      {children}
    </div>
  );
}
